/**
 * A6 How it works (English v0.1 · 2026-10-16) — 참여자 모두에게 보이는 사용자 가이드.
 * 블록: p(문단) · ul(목록, 하위 목록은 [글, [하위…]]) · ol(번호 목록) · table({ head, rows }).
 * 글 안의 **굵게** 는 굵은 글씨로 그린다.
 */
const HOW_IT_WORKS = {
  title: 'How it works',
  version: 'English v0.2 · Oct 23, 2026',
  intro: 'Your task is in the **Project brief**. This page explains how the 2days project works.',
  sections: [
    {
      heading: '1. What is the 2days project?',
      blocks: [
        {
          ul: [
            "You take on a task from a fictional Korean company. For 2 days, you work **like a real team**. Your team is the company's improvement task force.",
            'Work moves in this order: planning (PM) → design → development → QA. Together, you build one final product.',
            'Everything you do on the workboard is recorded: accepting, submitting, reviewing, commenting, and checking in. We use this record to create your **Growth Report** and your **Application Kit** for job applications.',
          ],
        },
      ],
    },
    {
      heading: '2. Timeline',
      blocks: [
        {
          table: {
            head: ['Time (T0 = PM start)', 'What happens'],
            rows: [
              ['T0', 'The PM (team lead) joins first and has 1 hour to plan the team schedule'],
              ['T0 + 1 hour', 'Team members join the workboard. Task notifications start'],
              ['Every 6 hours after team start (7 times)', 'Checkpoint (§7)'],
              ['T0 + 47 hours', 'Last task due'],
              ['T0 + 49 hours', 'Project ends (includes 2 hours to wrap up)'],
            ],
          },
        },
        {
          ul: [
            "**The 2 days are fixed.** Due times don't move. If you fall behind, cut the scope or split the work again.",
            'You shared your **offline hours** before the project. Everyone on your team sees them in Korea time (KST).',
          ],
        },
      ],
    },
    {
      heading: '3. Team and roles',
      blocks: [
        {
          ul: [
            'Each team has a **PM (team lead), designers, and developers**. If several people share a role, the system splits the tasks between them.',
            'Every task ends with a **handoff** to the next person. This person reviews your work. Then they approve it or send it back.',
          ],
        },
        {
          table: {
            head: ['Work', 'Who reviews it'],
            rows: [
              ['Planning document', 'Designer'],
              ['Design work', 'Developer'],
              ['Development work', 'PM (QA)'],
              [
                'Final product',
                'NPC team lead (the team lead of the company in the scenario, played by the Koready team)',
              ],
            ],
          },
        },
        { ul: ["You can't approve your own work."] },
      ],
    },
    {
      heading: '4. When you first join',
      blocks: [
        {
          ol: [
            'The **Scenario** window opens. It gives you a short summary of the task.',
            'Go to **Resources** and read the **Project brief** pinned at the top. It has the full task: clues, what to build, what not to build, deliverables, and how your work is evaluated.',
            "In Resources, you'll also find files for your role: current screens, sample data, guides, and templates. When a file changes, it shows an **Updated** label.",
          ],
        },
      ],
    },
    {
      heading: '5. How a task works',
      blocks: [
        {
          table: {
            head: ['Step', 'What you do', 'Good to know'],
            rows: [
              [
                '1. Assigned',
                'Read the task and its **task list** (the items the reviewer will check)',
                "Accept or decline soon. If you don't reply, the Koready team will contact you. The task may go to someone else",
              ],
              [
                '2. Accept or decline',
                'When you accept, set your **expected submit time** (before the due time). If you decline, give a reason',
                'Once you accept, the task is **in progress**. If a task has several assignees, only one person needs to accept',
              ],
              [
                '3. Work',
                "If you're stuck, post a **Blocker** comment",
                'You can start research or a rough draft before the previous step is approved',
              ],
              [
                '4. Submit',
                'Add your links, check every item on the task list, and write a one-line summary',
                "**You can submit only after the previous step is approved.** If a required link is missing, you can't submit",
              ],
              [
                '5. Review',
                'The reviewer marks each link and each task list item ✓ or ✗',
                'If everything is ✓, your work is **approved**',
              ],
              [
                '6. Sent back',
                'Read the comment that explains why. Fix your work and submit again',
                'Your new version is saved together with the earlier versions',
              ],
            ],
          },
        },
        { p: '**5 reasons work gets sent back**' },
        {
          ol: [
            "The link doesn't open, or there's no access",
            "It's not clear which version is final",
            'Some Task List items are missing',
            'Some decisions are still open',
            "It's not clear if earlier feedback was applied",
          ],
        },
        {
          p: 'Development work can also be sent back if the web link doesn’t load or the core flow breaks, the repository is private or empty, or the README is missing required sections.',
        },
      ],
    },
    {
      heading: '6. Expected submit time and delays',
      blocks: [
        {
          ul: [
            'You can only move your expected submit time **later**. The PM gets a notification each time.',
            "If you think you'll be late, update the time **at least 30 minutes before the due time**. If you submit by the new time, it's recorded as an **announced delay**.",
            'This works **once per task**. Everything else counts as **late**: a second change, missing the time you set, or being late without telling anyone.',
            "If the PM changes your task list after you accept, you can set your expected submit time one more time. It can't be after the due time.",
            "If you're never late, your Application Kit gets the **“Reports delays early”** badge.",
          ],
        },
      ],
    },
    {
      heading: '7. Checkpoints',
      blocks: [
        {
          ul: [
            "There's a checkpoint **every 6 hours after team start** (7 in total). Post one line about your progress and your expected submit time.",
            "Post by the checkpoint time: **on time**. Post after it: **late**. Don't post: **missed**.",
            "If a checkpoint falls in your offline hours, post **within 30 minutes after you're back online**. It still counts as on time.",
            'The PM can add extra checkpoints when needed.',
            [
              "**Dropping out:** The Koready team may mark you as dropped out if one of these happens. This is never decided automatically just because you didn't reply.",
              [
                "You **miss 2 checkpoints in a row** (checkpoints in your offline hours don't count)",
                "During your active hours, you **don't answer a question addressed to you for over 2 hours** (this includes messages from the Koready team)",
                "You tell us you're leaving",
              ],
            ],
          ],
        },
      ],
    },
    {
      heading: '8. Comments',
      blocks: [
        {
          table: {
            head: ['Type', 'When to use it'],
            rows: [
              [
                'Question',
                'To ask one specific person. You must pick 1 person. When you get an answer, mark it **Resolved**',
              ],
              [
                'Blocker',
                "When your task is stuck. When it's solved, pick who helped, or choose **Solved it myself**",
              ],
              ['General', 'To share something with the whole team or ask everyone'],
            ],
          },
        },
        {
          ul: [
            '**Your whole team** can see every comment. There are no private messages or chat.',
            "You **can't edit or delete** comments. To fix a typo, reply to your own comment.",
          ],
        },
      ],
    },
    {
      heading: '9. How to submit',
      blocks: [
        {
          ul: [
            'Paste your links into the **link fields** on the submit screen. Each field accepts certain types of links.',
            "**We don't accept Google Drive links.** Google Docs, Notion, and Figma links are fine.",
            "Make sure the reviewer **can view** your link. Set your GitHub repository to public. If a link doesn't open, your work will be sent back.",
            "Developers: the **Working web link** and **GitHub repository** are required to submit. The web link must open for anyone and stay live after the project ends — it's what goes in your portfolio.",
            'When you submit development work, we check that the web link opens, the repository is public and has a README at the root. If one fails, fix it before you submit. The reviewer also sees which README sections are missing.',
          ],
        },
        {
          table: {
            head: ['Who', 'Deliverables'],
            rows: [
              [
                'Whole team',
                '**1 working web link (required)** · One-page summary (4 questions, up to 1,500 characters each)',
              ],
              [
                'PM',
                'PRD (a draft when you hand off to the designer, the final version at final submission) · QA report',
              ],
              ['Designer', 'Figma file · Design system page'],
              [
                'Developer',
                'Working web link · Public GitHub repository · README at the repository root, written from **Resources › Templates › README_template** (problem and solution, how to run it, scope, data, tech stack and why, open-source libraries, your role, AI usage)',
              ],
            ],
          },
        },
        {
          ul: [
            'You can also add a Figma prototype or video link that shows how it works. This is optional.',
            'Templates are in **Resources › Templates**.',
          ],
        },
      ],
    },
    {
      heading: '10. AI tools',
      blocks: [
        {
          ul: [
            '**You can use AI tools at every step.**',
            'In your one-page summary, tell us in one line where you used AI.',
            'Developers: also write in your README which parts you built with AI and what you checked or changed by hand.',
          ],
        },
      ],
    },
    {
      heading: '11. Changes during the project',
      blocks: [
        {
          ul: [
            'The NPC team lead may announce changes during the project. This happens often in real companies too.',
            'How your team responds is also recorded (for example, cutting scope or splitting work again).',
          ],
        },
      ],
    },
    {
      heading: '12. Completing the project',
      blocks: [
        {
          ul: [
            '**You complete the project when you stay until the end (§7) and submit your peer review.**',
            "Submit your peer review **within 3 hours** after the final product goes to the NPC team lead. Your offline hours don't count.",
            "You can still submit after 3 hours, but your report will show “Late peer review.” If you haven't submitted it 24 hours after the project ends, you don't complete the project.",
            "When you complete the project, you get your **Growth Report**, your **Application Kit**, and a **completion link**. If you don't complete it, you won't get a report.",
          ],
        },
      ],
    },
  ],
};

export default HOW_IT_WORKS;
